// Feeds demuxed MP3 bytes into an HTMLAudioElement.
//
// Primary path: Media Source Extensions with an 'audio/mpeg' SourceBuffer — mpeg byte
// streams generate their own timestamps, so appending raw frames just works and unknown
// total duration is native (buffered end simply grows). This replaces the iOS
// AVAssetResourceLoader shim with its fake 4MB content-length + zero-padding; none of
// that is needed here.
//
// Fallback (Safari without MSE audio/mpeg): accumulate the whole part and play it as a
// Blob URL once the download completes. Playback starts late, but stays correct.
export class AudioSink {
  private mediaSource: MediaSource | null = null;
  private sourceBuffer: SourceBuffer | null = null;
  private objectUrl: string | null = null;

  private readonly appendQueue: Uint8Array[] = [];
  private streamEnded = false;
  private fallbackChunks: Uint8Array[] = [];
  // Set by the first append failure (or by halt()). The MSE pipeline cannot recover from a
  // failed SourceBuffer — the element's error attribute stays set, so every later
  // appendBuffer throws the same InvalidStateError. One user produced 2,424
  // playback_failed events in a day that way. Once failed, the sink drops its queue and
  // ignores every further chunk and end() until the next beginPart() resets it.
  private failed = false;

  private onReadyForPlayback: (() => void) | null = null;

  constructor(
    private readonly audio: HTMLAudioElement,
    private readonly onError: (message: string) => void
  ) {}

  static supportsProgressive(): boolean {
    return (
      typeof MediaSource !== 'undefined' &&
      typeof MediaSource.isTypeSupported === 'function' &&
      MediaSource.isTypeSupported('audio/mpeg')
    );
  }

  // Progressive sinks can play while downloading; the fallback becomes playable only
  // after end().
  readonly progressive = AudioSink.supportsProgressive();

  // Resolves when the element has enough to start playing (immediately on MSE).
  beginPart(onReadyForPlayback: () => void): void {
    this.reset();
    this.onReadyForPlayback = onReadyForPlayback;

    if (!this.progressive) {
      return;
    }

    const source = new MediaSource();
    this.mediaSource = source;
    this.objectUrl = URL.createObjectURL(source);

    source.addEventListener(
      'sourceopen',
      () => {
        // A queued sourceopen can fire after a rapid part switch already replaced the
        // MediaSource — acting on the live sink state then would corrupt the NEW part.
        if (this.mediaSource !== source) {
          return;
        }

        let buffer: SourceBuffer;

        try {
          buffer = source.addSourceBuffer('audio/mpeg');
        } catch (error) {
          this.fail(`addSourceBuffer failed: ${String(error)}`);
          return;
        }

        this.sourceBuffer = buffer;
        buffer.addEventListener('updateend', () => this.pumpQueue());
        buffer.addEventListener('error', () => {
          // A queued append error from a part that was already torn down must not fail
          // whatever part is playing now.
          if (this.sourceBuffer === buffer) {
            this.fail('SourceBuffer append error');
          }
        });
        this.pumpQueue();
        this.onReadyForPlayback?.();
        this.onReadyForPlayback = null;
      },
      { once: true }
    );

    this.audio.src = this.objectUrl;
  }

  append(chunk: Uint8Array): void {
    if (this.failed) {
      return;
    }

    if (this.progressive) {
      this.appendQueue.push(chunk);
      this.pumpQueue();
    } else {
      this.fallbackChunks.push(chunk);
    }
  }

  end(): void {
    if (this.failed) {
      return;
    }

    this.streamEnded = true;

    if (this.progressive) {
      this.pumpQueue();
    } else {
      const blob = new Blob(this.fallbackChunks as BlobPart[], { type: 'audio/mpeg' });
      this.fallbackChunks = [];
      this.objectUrl = URL.createObjectURL(blob);
      this.audio.src = this.objectUrl;
      this.onReadyForPlayback?.();
      this.onReadyForPlayback = null;
    }
  }

  bufferedEnd(): number {
    const buffered = this.audio.buffered;

    return buffered.length ? buffered.end(buffered.length - 1) : 0;
  }

  // True once no more audio can arrive — the buffered end is final. On MSE that is only
  // after endOfStream() has actually run (the append queue drains asynchronously, so
  // "download done" alone is NOT final); the fallback is final once the blob is set.
  finalized(): boolean {
    if (this.progressive) {
      return this.mediaSource !== null && this.mediaSource.readyState === 'ended';
    }

    return this.streamEnded;
  }

  // Stops feeding the element without reporting: the owner already learned about the
  // failure from the element itself (its 'error' event), and a second report from the next
  // doomed appendBuffer would only duplicate it.
  halt(): void {
    this.failed = true;
    this.appendQueue.length = 0;
    this.fallbackChunks = [];
  }

  dispose(): void {
    this.reset();
  }

  // First failure wins; the sink reports once and goes quiet until the next beginPart().
  private fail(message: string): void {
    if (this.failed) {
      return;
    }

    this.halt();
    this.onError(message);
  }

  private pumpQueue(): void {
    const buffer = this.sourceBuffer;

    if (
      this.failed ||
      !buffer ||
      buffer.updating ||
      !this.mediaSource ||
      this.mediaSource.readyState !== 'open'
    ) {
      return;
    }

    const chunk = this.appendQueue.shift();

    if (chunk) {
      try {
        buffer.appendBuffer(chunk as BufferSource);
      } catch (error) {
        this.fail(`appendBuffer failed: ${String(error)}`);
      }
      return;
    }

    if (this.streamEnded) {
      // Locks the real duration to the buffered end; the element fires 'ended' at the
      // true tail instead of a believed length.
      try {
        this.mediaSource.endOfStream();
      } catch {
        // The element may already have detached the source during teardown.
      }
    }
  }

  private reset(): void {
    this.appendQueue.length = 0;
    this.fallbackChunks = [];
    this.streamEnded = false;
    this.failed = false;
    this.sourceBuffer = null;
    this.onReadyForPlayback = null;

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    this.mediaSource = null;
    this.audio.removeAttribute('src');
    this.audio.load();
  }
}
